import { Component, computed, effect, inject, signal, ViewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { AuthorizationService } from '../../../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../../../core/services/authorization/authorization-state.service';
import { ThemeService } from '../../../../core/services/theme.service';
import { DashboardService } from '../../../../core/services/dashboard/dashboard.service';
import { MemberService } from '../../../../core/services/member.service';
import { DashboardStatCardComponent } from '../dashboard-stat-card/dashboard-stat-card.component';
import { DashboardQuickActionsComponent } from '../dashboard-quick-actions/dashboard-quick-actions.component';
import { DashboardRecentListComponent } from '../dashboard-recent-list/dashboard-recent-list.component';
import { DashboardChartComponent } from '../dashboard-chart/dashboard-chart.component';
import { DashboardAdminListComponent } from '../dashboard-admin-list/dashboard-admin-list.component';
import { DashboardSelectOrgPromptComponent } from '../dashboard-select-org-prompt/dashboard-select-org-prompt.component';
import { DashboardMemberProfileCardComponent } from '../dashboard-member-profile-card/dashboard-member-profile-card.component';
import { PropertyChargeListComponent } from '../../../balances/components/property-charge-list/property-charge-list.component';
import { PropertyChargeListViewLocations } from '../../../../core/enums/base.enums';
import { PaymentModalComponent } from '../../../../parts/payment-modal/payment-modal.component';
import { CurrencyDisplayPipe } from '../../../../core/pipes/currency/currency.pipe';
import { CurrencyModel } from '../../../../core/models/data/currency.model';
import { MemberModel } from '../../../../core/models/data/member.model';
import { MemberUtilities } from '../../../../core/utilities/member.utilities';
import { DashboardOrganizationStats, DashboardRecentItem, MemberInvoiceSummary } from '../../../../core/models/ui/dashboard.model';
import { DashboardConstants } from '../../../../core/constants/dashboard.constants';
import { DashboardChartConfig, DashboardChartUtilities } from '../../../../core/utilities/dashboard-chart.utilities';
import { BalancesPermission, MemberPermission, Permission, PropertyPermission, RolePermission } from '../../../../core/enums/permissions.enums';
import { IfHasPermissionDirective } from '../../../../core/directives/if-has-permission/if-has-permission.directive';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';
import { PaymentMessagingConstants } from '../../../../core/constants/messaging.constants';

@Component({
	selector: 'ms-org-dashboard',
	imports: [
		RouterLink,
		FaDuotoneIconComponent,
		DashboardStatCardComponent,
		DashboardQuickActionsComponent,
		DashboardRecentListComponent,
		DashboardChartComponent,
		DashboardAdminListComponent,
		DashboardSelectOrgPromptComponent,
		DashboardMemberProfileCardComponent,
		PropertyChargeListComponent,
		PaymentModalComponent,
		CurrencyDisplayPipe,
		IfHasPermissionDirective,
		TooltipDirective
	],
	templateUrl: './org-dashboard.component.html',
	styleUrl: './org-dashboard.component.scss'
})
export class OrgDashboardComponent {
	@ViewChild('chargeList') chargeList?: PropertyChargeListComponent;
	@ViewChild('paymentModal') paymentModal?: PaymentModalComponent;

	//#region Permission Signals

	private hasOrgPermission(permission: Permission) {
		return computed(() => {
			const authState = this.authorizationStateService.authorizationState();
			const org = this.userSessionService.activeOrganization();
			if (!authState || !org) return false;
			return this.authorizationService.hasPermission(authState, permission, org.organizationId);
		});
	}

	public canReadMembers = this.hasOrgPermission(MemberPermission.Read);
	public canReadDeletedMembers = this.hasOrgPermission(MemberPermission.ReadDeleted);
	public canReadProperties = this.hasOrgPermission(PropertyPermission.Read);
	public canReadBalances = this.hasOrgPermission(BalancesPermission.Read);
	public canManageAdminRole = this.hasOrgPermission(RolePermission.OrgAdmin);

	public hasAdminPermissions = computed(() => this.canReadMembers() || this.canReadProperties());

	//#endregion

	//#region Data Signals

	public member = signal<MemberModel | undefined>(undefined);
	public orgStats = signal<DashboardOrganizationStats | undefined>(undefined);
	public recentMembers = signal<DashboardRecentItem[]>([]);
	public recentProperties = signal<DashboardRecentItem[]>([]);
	public myProperties = signal<DashboardRecentItem[]>([]);
	public invoiceSummary = signal<MemberInvoiceSummary | undefined>(undefined);
	public selectedInvoiceAmount = signal<number>(0);
	public currencyType = signal<CurrencyModel | undefined>(undefined);

	public invoiceStatusData = signal<DashboardChartConfig | undefined>(undefined);
	public memberStatusData = signal<DashboardChartConfig | undefined>(undefined);

	// Starts true so the card renders its skeleton on first paint: the load is kicked off by the
	// effect below, which does not run until after the first change detection.
	public isLoadingMember = signal(true);
	public isLoadingStats = signal(false);
	public isLoadingMembers = signal(false);
	public isLoadingProperties = signal(false);
	public isLoadingMyProperties = signal(false);
	public isLoadingInvoiceSummary = signal(false);
	public isLoadingInvoiceStatus = signal(false);
	public isLoadingMemberStatus = signal(false);

	//#endregion

	//#region Constants

	public DashboardConstants = DashboardConstants;
	public PropertyChargeListViewLocations = PropertyChargeListViewLocations;
	public MemberPermission = MemberPermission;
	public PropertyPermission = PropertyPermission;
	public BalancesPermission = BalancesPermission;
	public RolePermission = RolePermission;
	public quickActions = DashboardConstants.ADMIN_QUICK_ACTIONS;
	public onlinePaymentsDisabledTooltip = PaymentMessagingConstants.PAYMENT_ONLINE_PAYMENTS_DISABLED_TOOLTIP();

	//#endregion

	//#region Computed

	public memberId = computed<number | undefined>(() => {
		const authState = this.authorizationStateService.authorizationState();
		const activeOrg = this.userSessionService.activeOrganization();
		if (!authState || !activeOrg) return undefined;
		const memberPermission = authState.memberPermissions
			?.find(mp => mp.organizationId === activeOrg.organizationId);
		return memberPermission?.memberId;
	});

	public memberProfileLink = computed<string | undefined>(() => {
		const id = this.memberId();
		return id ? `/members/profile/${id}` : undefined;
	});

	// No membership for the active org, or a failed fetch, drops the card and gives the organization
	// card the whole row rather than leaving half of it empty.
	public showMemberCard = computed<boolean>(() => !!this.member() || this.isLoadingMember());

	public orgProfileLink = computed<string | undefined>(() => {
		const org = this.userSessionService.activeOrganization();
		return org ? `/organizations/profile/${org.organizationId}` : undefined;
	});

	public isStripeOnboardingComplete = computed(() =>
		this.userSessionService.activeOrganizationSettings()?.isStripeOnboardingComplete ?? false);

	//#endregion

	private dashboardService = inject(DashboardService);
	private memberService = inject(MemberService);
	public userSessionService = inject(UserSessionService);
	private authorizationService = inject(AuthorizationService);
	private authorizationStateService = inject(AuthorizationStateService);
	private themeService = inject(ThemeService);

	private hasLoadedChargeList = false;

	constructor() {
		effect(() => {
			const org = this.userSessionService.activeOrganization();
			const memberId = this.memberId();
			if (org && memberId) {
				this.loadData(org.organizationId, memberId);

				// The charge list builds its own org + member filter in ngOnInit, so the first pass
				// would otherwise fire the same query twice. Later passes are org switches, which
				// leave the @if truthy and so never re-create the component — those still need the
				// filter pushed in imperatively.
				if (this.hasLoadedChargeList) {
					this.reloadChargeList();
				}
				this.hasLoadedChargeList = true;
			} else {
				// No member record for the active org destroys the charge list, so the next org that
				// does have one re-mounts it and ngOnInit self-configures again.
				this.hasLoadedChargeList = false;
				this.member.set(undefined);
				this.isLoadingMember.set(false);
				this.selectedInvoiceAmount.set(0);
				this.currencyType.set(undefined);
			}
		});
	}

	private reloadChargeList(): void {
		setTimeout(() => {
			if (!this.chargeList) return;
			const org = this.userSessionService.activeOrganization();
			const memberId = this.memberId();
			if (org && memberId) {
				this.chargeList.propertyChargeFilter.organizationIds = [org.organizationId];
				this.chargeList.propertyChargeFilter.memberIds = [memberId];
				this.chargeList.loadPropertyCharges(false);
			}
		}, 0);
	}

	//#region Data Loading

	private async loadData(organizationId: number, memberId: number): Promise<void> {
		// Always load (all org members have payments.*)
		this.loadMember(memberId);
		this.loadInvoiceData(organizationId, memberId);
		this.loadMyProperties(organizationId, memberId);

		// Only load if user has permissions
		if (this.canReadMembers() || this.canReadProperties()) {
			this.loadOrgStats(organizationId);
		}
		if (this.canReadMembers()) {
			this.loadRecentMembers(organizationId);
		}
		if (this.canReadProperties()) {
			this.loadRecentProperties(organizationId);
		}
		if (this.canReadBalances() || this.canReadMembers()) {
			this.loadCharts(organizationId);
		}
	}

	private async loadMember(memberId: number): Promise<void> {
		this.isLoadingMember.set(true);
		this.member.set(undefined);

		const result = await this.memberService.getMemberById(memberId);

		// An org switch starts a newer load, so a response for an organization we have already left is
		// dropped rather than painted over the new one.
		if (this.memberId() !== memberId) return;

		this.member.set(result.success && result.data
			? MemberUtilities.withNormalizedPhone(result.data)
			: undefined);
		this.isLoadingMember.set(false);
	}

	private async loadOrgStats(organizationId: number): Promise<void> {
		this.isLoadingStats.set(true);
		const result = await this.dashboardService.getOrganizationStats(organizationId);
		if (result.success) {
			this.orgStats.set(result.data);
		}
		this.isLoadingStats.set(false);
	}

	private async loadRecentMembers(organizationId: number): Promise<void> {
		this.isLoadingMembers.set(true);
		const result = await this.dashboardService.getRecentMembers(organizationId);
		if (result.success) {
			this.recentMembers.set(result.data ?? []);
		}
		this.isLoadingMembers.set(false);
	}

	private async loadRecentProperties(organizationId: number): Promise<void> {
		this.isLoadingProperties.set(true);
		const result = await this.dashboardService.getRecentProperties(organizationId);
		if (result.success) {
			this.recentProperties.set(result.data ?? []);
		}
		this.isLoadingProperties.set(false);
	}

	private async loadMyProperties(organizationId: number, memberId: number): Promise<void> {
		this.isLoadingMyProperties.set(true);
		const result = await this.dashboardService.getMemberProperties(organizationId, [memberId]);
		if (result.success) {
			this.myProperties.set(result.data ?? []);
		}
		this.isLoadingMyProperties.set(false);
	}

	private async loadInvoiceData(organizationId: number, memberId: number): Promise<void> {
		this.isLoadingInvoiceSummary.set(true);
		const result = await this.dashboardService.getMemberInvoiceSummary(organizationId, memberId);
		if (result.success) {
			this.invoiceSummary.set(result.data);
		}
		this.isLoadingInvoiceSummary.set(false);
	}

	private async loadCharts(organizationId: number): Promise<void> {
		if (this.canReadBalances()) {
			this.isLoadingInvoiceStatus.set(true);
			this.dashboardService.getInvoiceStatusCounts(organizationId).then(result => {
				if (result.success && result.data) {
					this.invoiceStatusData.set(DashboardChartUtilities.buildInvoiceStatusChartData(result.data, this.themeService));
				}
				this.isLoadingInvoiceStatus.set(false);
			});
		}

		if (this.canReadMembers()) {
			this.isLoadingMemberStatus.set(true);

			this.dashboardService.getMemberStatusCounts(organizationId, this.canReadDeletedMembers()).then(result => {
				if (result.success && result.data) {
					this.memberStatusData.set(DashboardChartUtilities.buildMemberStatusChartData(result.data, this.themeService));
				}
				this.isLoadingMemberStatus.set(false);
			});
		}
	}

	//#endregion

	//#region Payment

	public async payCharges(): Promise<void> {
		const charges = this.chargeList?.buildChargeBulkActionRequest();
		if (!charges) return;
		await this.paymentModal?.open(
			charges,
			this.currencyType()?.code,
			this.selectedInvoiceAmount()
		);
	}

	public onPaymentCompleted(): void {
		this.chargeList?.loadPropertyCharges(false);
	}

	//#endregion
}
